import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IncomeReceiptsService } from '../../services/income-receipts.service';
import { LoadingService } from '../../services/loading.service';
import { ToastService } from '../../services/toast.service';
import { ConfirmationConfig } from '../shared/confirmation-modal/confirmation-modal.component';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-income-receipts',
  templateUrl: './income-receipts.component.html',
  styleUrls: ['./income-receipts.component.scss']
})
export class IncomeReceiptsComponent implements OnInit, OnDestroy {
  incomeReceipts: any[] = [];
  filteredReceipts: any[] = [];

  // Debounce
  private filterSubject = new Subject<void>();
  private destroy$ = new Subject<void>();

  // Vista fija para individuales
  view: 'individual' = 'individual';

  // Filtros
  searchTerm: string = '';
  filterId: number | null = null;
  selectedPaymentMethod: any = null; // 🔄 Cambiado para p-dropdown
  selectedPaymentType: any = null; // 🔄 Cambiado para p-dropdown
  selectedCancellationFilter: string = 'active'; // 🆕 Filtro de anulación

  /**
   * 🆕 Filtro adicional por proyecto (numeric id). Se setea cuando el deep-link
   * proviene del módulo de auditoría de planes con `?projectId=NN`. Se aplica
   * client-side sobre los resultados ya paginados del servidor (el backend
   * todavía no soporta este filtro). Útil para evitar que los comprobantes de
   * otros proyectos del mismo participante "contaminen" la búsqueda.
   */
  filterProjectId: number | null = null;

  // 🆕 Filtros de fecha
  startDate: Date | null = null;
  endDate: Date | null = null;
  dateType: string = 'Creation'; // 'Creation' o 'Payment'
  dateTypeOptions: any[] = [
    { label: 'Fecha de Creación', value: 'Creation' },
    { label: 'Fecha de Pago', value: 'Payment' }
  ];

  // 🆕 Ordenamiento
  sortOrder: 'asc' | 'desc' = 'desc'; // Por defecto descendente (más recientes primero)

  // 🆕 Paginación
  pageNumber: number = 1;
  pageSize: number = 20;
  totalRecords: number = 0;
  paginatedReceipts: any[] = [];

  // Métodos de pago únicos
  paymentMethods: any[] = [];

  // 🆕 Modal de confirmación de anulación
  showConfirmationModal = false;
  cancellationReason = '';
  selectedReceiptForCancellation: any = null;

  confirmationConfig: ConfirmationConfig = {
    title: '¿Anular comprobante de ingreso?',
    message: 'Esta acción es irreversible. El comprobante será anulado y no se incluirá en reportes ni sumas.',
    confirmText: 'Anular Comprobante',
    cancelText: 'Cancelar',
    type: 'danger',
    icon: 'fa-ban',
    showIcon: true
  };

  // Métricas generales
  totalGross: number = 0;
  totalNet: number = 0;
  totalDiscounts: number = 0;
  avgPaymentSpeed: number = 0;

  // Métricas específicas para vista individual
  individualReceipts: number = 0;

  constructor(
    private incomeReceiptsService: IncomeReceiptsService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    // 🆕 Acepta query param ?search=XXX para precargar el filtro de búsqueda
    // (útil para deep-links desde otros dashboards, ej: payment plan audit).
    const initialSearch = this.route.snapshot.queryParamMap.get('search');
    if (initialSearch) {
      this.searchTerm = initialSearch;
    }

    // 🆕 ?projectId=NN — co-filtro por proyecto (client-side). Llega desde
    // payment-plan-audit para acotar los pagos del mismo documento a un único
    // proyecto y evitar confusiones cuando el participante aparece en varios.
    const initialProjectId = this.route.snapshot.queryParamMap.get('projectId');
    if (initialProjectId && !isNaN(Number(initialProjectId))) {
      this.filterProjectId = Number(initialProjectId);
    }

    this.filterSubject.pipe(
      debounceTime(400),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.pageNumber = 1;
      this.loadData();
    });

    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Método removido - vista fija individual

  // 🆕 MÉTODOS DE IDENTIFICACIÓN BASADOS EN PayerType
  isIndividualReceipt(receipt: any): boolean {
    // Verificar primero si existe payerType (nuevo backend)
    if (receipt.payerType !== undefined) {
      return receipt.payerType === 'PARTICIPANT';
    }
    // Fallback para datos existentes (solo participant, no company)
    return receipt.participant !== null && !receipt.companyId;
  }

  isCorporateReceipt(receipt: any): boolean {
    // Verificar primero si existe payerType (nuevo backend) 
    if (receipt.payerType !== undefined) {
      return receipt.payerType === 'COMPANY';
    }
    // Fallback para datos existentes (no participant, pero puede tener company info)
    return receipt.participant === null;
  }

  isParticipantCorporateReceipt(receipt: any): boolean {
    // Verificar primero si existe payerType (nuevo backend)
    if (receipt.payerType !== undefined) {
      return receipt.payerType === 'PARTICIPANT_CORPORATE';
    }
    // Fallback para datos existentes (participant Y company)  
    return receipt.participant !== null && receipt.companyId;
  }

  getPayerDisplayText(receipt: any): string {
    if (receipt.payerDisplayName) {
      return receipt.payerDisplayName; // 🆕 Usar el campo calculado del backend
    }

    // 🔄 FALLBACK para datos existentes
    if (this.isParticipantCorporateReceipt(receipt)) {
      const participantName = `${receipt.participant?.firstName || ''} ${receipt.participant?.lastName || ''}`.trim();
      const companyName = receipt.companyName || receipt.company?.name || 'Empresa';
      return `${participantName} (${companyName})`;
    } else if (this.isCorporateReceipt(receipt)) {
      return receipt.companyName || receipt.company?.name || 'Pago Empresarial';
    } else {
      return `${receipt.participant?.firstName || ''} ${receipt.participant?.lastName || ''}`.trim() || 'Participante';
    }
  }

  private loadData(): void {
    this.loadingService.show();

    // 🆕 USAR SERVICIO PAGINADO DEL LADO SERVIDOR
    const parameters: any = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      searchTerm: this.searchTerm,
      sortBy: 'Id',
      sortOrder: this.sortOrder,
      startDate: this.startDate,
      endDate: this.endDate,
      dateType: this.dateType,
      cancellationFilter: this.selectedCancellationFilter,
      paymentMethodId: this.selectedPaymentMethod,
      isManualPayment: this.selectedPaymentType,
      payerType: 'PARTICIPANT' // Solo individuales en esta vista
    };

    if (this.filterId) parameters.filterId = this.filterId;

    this.incomeReceiptsService.getIncomeReceiptsPaged(parameters).subscribe({
      next: (response) => {
        this.incomeReceipts = response.data || [];
        this.totalRecords = response.totalRecords || 0;

        // 🆕 Aplicar co-filtro por proyecto (client-side). El backend aún no
        // soporta filtrar por projectId, así que filtramos el page actual.
        // Limitación conocida: la cuenta total puede sobrestimar cuando hay
        // mezcla de proyectos en otras páginas. En la práctica el deep-link
        // ya viene acotado por documento, lo cual reduce el set drásticamente.
        if (this.filterProjectId) {
          this.incomeReceipts = this.incomeReceipts.filter(
            (r: any) => r?.project?.id === this.filterProjectId
          );
          this.totalRecords = this.incomeReceipts.length;
        }

        this.paginatedReceipts = this.incomeReceipts; // Ya viene paginado del servidor

        this.processData();
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error al cargar comprobantes paginados:', error);
        this.toastService.showToast('error', 'Error al cargar los datos');
        this.loadingService.hide();
      }
    });
  }

  private processData(): void {
    // 🆕 CALCULAR MÉTRICAS ESPECÍFICAS PARA VISTA INDIVIDUAL
    this.individualReceipts = this.incomeReceipts.filter(r => this.isIndividualReceipt(r)).length;

    // Obtener métodos de pago únicos
    const uniqueMethodIds = [...new Set(this.incomeReceipts
      .filter(r => r.paymentMethod) // Filtrar receipts que tengan paymentMethod
      .map(r => r.paymentMethod.id))];

    this.paymentMethods = uniqueMethodIds.map(id =>
      this.incomeReceipts.find(r => r.paymentMethod && r.paymentMethod.id === id)?.paymentMethod
    ).filter(method => method); // Filtrar métodos undefined

    // Calcular métricas para la vista actual
    this.calculateCurrentViewMetrics();
  }

  private calculateCurrentViewMetrics(): void {
    // 🆕 FILTRAR DATOS SOLO PARA VISTA INDIVIDUAL
    const currentViewData = this.incomeReceipts.filter(receipt =>
      this.isIndividualReceipt(receipt)
    );

    // Calcular métricas para la vista actual
    this.totalGross = currentViewData.reduce((sum, r) => sum + r.grossAmount, 0);
    this.totalNet = currentViewData.reduce((sum, r) => sum + r.netAmount, 0);
    this.totalDiscounts = currentViewData.reduce((sum, r) => sum + r.totalFees, 0);

    // Calcular velocidad promedio de pago para vista actual
    const totalDays = currentViewData
      .filter(r => r.processingDays)
      .reduce((sum, r) => sum + r.processingDays, 0);
    this.avgPaymentSpeed = totalDays / currentViewData.length || 0;
  }

  // 🆕 Método para obtener documento del participante
  getParticipantDocument(receipt: any): string {
    if (receipt.participant) {
      return `${receipt.participant.typeDocument || ''} ${receipt.participant.documentId || ''}`.trim();
    }
    return 'N/A';
  }

  // 🆕 MÉTODOS PARA MANEJO DE ANULACIÓN
  showCancelModal(receipt: any): void {
    if (receipt.isCancelled) {
      this.toastService.showToast('warning', 'Este comprobante ya está anulado');
      return;
    }

    this.selectedReceiptForCancellation = receipt;
    this.cancellationReason = '';
    this.showConfirmationModal = true;
  }

  async onConfirmCancellation(): Promise<void> {
    if (!this.cancellationReason?.trim()) {
      this.toastService.showToast('error', 'Debe especificar el motivo de anulación');
      return;
    }

    if (!this.selectedReceiptForCancellation) {
      return;
    }

    try {
      this.loadingService.show();

      await this.incomeReceiptsService.cancelIncomeReceipt(
        this.selectedReceiptForCancellation.id,
        this.cancellationReason.trim()
      ).toPromise();

      this.toastService.showToast('success', '✅ Comprobante anulado exitosamente');
      this.closeConfirmationModal();
      this.loadData(); // Recargar datos para reflejar cambios

    } catch (error: any) {
      console.error('Error al anular comprobante:', error);
      const errorMessage = error?.error?.errorMessage ||
        error?.error ||
        'Error al anular el comprobante';
      this.toastService.showToast('error', errorMessage);
    } finally {
      this.loadingService.hide();
    }
  }

  onCancelCancellation(): void {
    this.closeConfirmationModal();
  }

  private closeConfirmationModal(): void {
    this.showConfirmationModal = false;
    this.selectedReceiptForCancellation = null;
    this.cancellationReason = '';
  }

  getPaymentProgress(receipt: any): number {
    return (receipt.netAmount / receipt.grossAmount) * 100;
  }

  // 🆕 Método para formatear montos con moneda específica del comprobante
  formatCurrencyValue(amount: number, receipt: any): string {
    if (!amount && amount !== 0) return '';

    // Prioridad: moneda del comprobante > COP por defecto
    const currencySymbol = receipt?.currencySymbol || '$';
    const currencyCode = receipt?.currencyCode || 'COP';

    const formattedAmount = new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);

    return `${currencySymbol}${formattedAmount} (${currencyCode})`;
  }

  // 🆕 Verificar si el comprobante tiene tasa de cambio internacional
  hasExchangeRate(receipt: any): boolean {
    return receipt?.exchangeRate && receipt.exchangeRate !== null && receipt.exchangeRate !== 1;
  }

  // 🆕 Obtener el equivalente en COP si hay tasa de cambio
  getCOPEquivalent(amount: number, receipt: any): string {
    if (!this.hasExchangeRate(receipt) || !receipt?.exchangeRate) {
      return '';
    }

    const copAmount = amount * receipt.exchangeRate;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(copAmount);
  }

  // 🆕 MÉTODOS DE DESCARGA DE PDF
  isDownloadingPdf: { [key: number]: boolean } = {};

  downloadPdf(receipt: any): void {
    if (this.isDownloadingPdf[receipt.id]) return;

    this.isDownloadingPdf[receipt.id] = true;
    this.incomeReceiptsService.downloadPdf(receipt.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ComprobanteIngreso_${receipt.id}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.isDownloadingPdf[receipt.id] = false;
        this.toastService.showToast('success', 'PDF descargado correctamente');
      },
      error: (error) => {
        console.error('Error downloading PDF:', error);
        this.isDownloadingPdf[receipt.id] = false;
        this.toastService.showToast('error', 'Error al descargar el PDF');
      }
    });
  }

  // 🆕 Método para cambio de página (ahora del lado servidor)
  onPageChange(event: any): void {
    this.pageNumber = event.page + 1;
    this.pageSize = event.rows;
    this.loadData();
  }

  // 🆕 Método para cambiar ordenamiento
  toggleSortOrder(): void {
    this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc';
    this.loadData();
  }

  // Los filtros ahora se aplican del lado servidor con debounce
  onFilterChange(): void {
    this.filterSubject.next();
  }

  // 🆕 Método para cambio de filtro de anulación
  onCancellationFilterChange(): void {
    this.pageNumber = 1;
    this.loadData();
  }

  // 🆕 MÉTODOS PARA INDICADORES DE APROBACIÓN
  getApprovalStatusInfo(receipt: any): { label: string; cssClass: string; icon: string } | null {
    if (!receipt.approvalStatus || receipt.approvalStatus === 'None') {
      return null;
    }
    switch (receipt.approvalStatus) {
      case 'PendingApproval':
        return { label: 'Pendiente Aprobación', cssClass: 'bg-yellow-100 text-yellow-800', icon: 'fa-clock' };
      case 'Approved':
        return { label: 'Aprobado', cssClass: 'bg-green-100 text-green-800', icon: 'fa-check-circle' };
      case 'Rejected':
        return { label: 'Rechazado', cssClass: 'bg-red-100 text-red-800', icon: 'fa-times-circle' };
      default:
        return null;
    }
  }

  isPendingApproval(receipt: any): boolean {
    return receipt.approvalStatus === 'PendingApproval';
  }

  hasVersionInfo(receipt: any): boolean {
    return receipt.version && receipt.version > 1;
  }

  // 🆕 MÉTODOS PARA APROBACIÓN/RECHAZO DE EDICIONES
  showApprovalConfirmationModal = false;
  selectedReceiptForApproval: any = null;
  approvalAction: string = '';
  approvalNotes: string = '';
  isProcessingApproval = false;

  showApprovalModal(receipt: any, action: string): void {
    this.selectedReceiptForApproval = receipt;
    this.approvalAction = action;
    this.approvalNotes = '';
    this.showApprovalConfirmationModal = true;
  }

  onCancelApproval(): void {
    this.showApprovalConfirmationModal = false;
    this.selectedReceiptForApproval = null;
    this.approvalAction = '';
    this.approvalNotes = '';
  }

  onConfirmApproval(): void {
    if (!this.selectedReceiptForApproval || !this.approvalAction) return;
    if (this.approvalAction === 'Reject' && !this.approvalNotes?.trim()) return;

    this.isProcessingApproval = true;

    this.incomeReceiptsService.processApproval(
      this.selectedReceiptForApproval.id,
      this.approvalAction,
      this.approvalNotes?.trim() || ''
    ).subscribe({
      next: (response: any) => {
        const actionLabel = this.approvalAction === 'Approve' ? 'aprobada' : 'rechazada';
        this.toastService.showToast('success', `Edición ${actionLabel} exitosamente`);
        this.isProcessingApproval = false;
        this.showApprovalConfirmationModal = false;
        this.selectedReceiptForApproval = null;
        this.approvalNotes = '';
        this.loadData();
      },
      error: (error: any) => {
        console.error('Error processing approval:', error);
        this.toastService.showToast('error', 'Error al procesar la aprobación');
        this.isProcessingApproval = false;
      }
    });
  }
}
